import useSWR, { Fetcher } from 'swr';

import { deepCamelCase, NetworkError } from '@vimeo/gctl-api/fetchers/_helpers';
import { useViewer } from '@vimeo/viewer';
import { getSearchParams } from '@vimeo/viewer-showcase-ui/utils/helper';

export const ShowcaseAuthErrorCode = {
  passwordPrivacy: 0,
  incorrectPassword: 1,
  teamPrivacy: 2,
  teamPrivacyLoggedInUser: 3,
  embedOnly: 4,
  unknownReason: 5,
  unlisted: 6,
} as const;

/**
 * Metadata is used to render alternative showcase views, e,g BrandedLoginPage
 * It's coming from `Album/AuthorizationController::authorize`
 */
export interface AlbumMetadata {
  /** album title, e.g 'New showcase' */
  name: string;
  /** Album accent color config, e.g 'efa700' */
  brandColor: string | null;
  /** Album owner id, e.g 105616991 */
  creator: number;
  /** ? this might be never set ? */
  url: string | null;
  /** ?offsite domain */
  domain: string | null;
  /** Album custom logo url */
  customLogo: string | null;
  /** Whether Album support SSO Login */
  hasSso: boolean;
  /** Album theme config, if it's not dark, it's locked in light theme */
  theme: 'standard' | 'dark';
  /** Album id, e.g 11421939 */
  id: number;
  /** Album owner's SSO connection counts */
  ssoConnectionsCount: number;
  /** Album layout config */
  layout: string | null;
}

export interface ShowcaseAuthResponse {
  errorCode: (typeof ShowcaseAuthErrorCode)[keyof typeof ShowcaseAuthErrorCode] | null;
  metadata: AlbumMetadata;
  hashedPass?: string | null; // won't be included in the response if it's non 200
}

export interface ShowcaseAuthError {
  res: ShowcaseAuthResponse;
  status: number;
}

/**
 * Result of parsing a /showcase/{id}/auth response.
 * Shared between the client-side SWR fetcher and SSR getServerSideProps
 * so both paths handle the response identically.
 */
export interface ShowcaseAuthParsedResponse {
  status: number;
  ok: boolean;
  /** Parsed + camelCased response body. Absent only on 404 (no JSON body). */
  data?: ShowcaseAuthResponse;
}

/**
 * Parse a showcase auth endpoint response: status check → json → deepCamelCase.
 * Does NOT handle redirects (3xx) — callers must check for those before calling.
 */
export async function parseShowcaseAuthResponse(
  res: Response,
): Promise<ShowcaseAuthParsedResponse> {
  if (res.status === 404) {
    return { status: 404, ok: false };
  }

  const json = await res.json();
  const data = deepCamelCase(json) as ShowcaseAuthResponse;

  return { status: res.status, ok: res.ok, data };
}

export const useShowcaseAuth = (
  albumIdOrUrl: string | number,
  options: { isShowcaseEmbedded?: boolean; fallbackData?: ShowcaseAuthResponse },
) => {
  const viewer = useViewer();
  const sh = getSearchParams().get('sh'); // album hash

  return useSWR<ShowcaseAuthResponse, ShowcaseAuthError>(
    () => {
      if (!viewer) return null;

      const authQueryParams = {
        ...(sh && { sh }),
        ...(options?.isShowcaseEmbedded && {
          referrer: encodeURIComponent(getReferrerValue() || ''),
          is_embed: true,
        }),
      };

      /**
       * This is not Vimeo API endpoint, hence no GCTL hook generated, use SWR directly
       * Ref: https://github.com/vimeows/vimeo/blob/f054bd8cee34386203217ef0b28718bcef8b53f8/routes/main.php#L4565-L4569
       */
      const authUrl = albumIdOrUrl
        ? `/showcase/${albumIdOrUrl}/auth${serializeQuery(authQueryParams)}`
        : '/auth'; // For showcase custom domain, such as "http://vodcontainer.com/", there won't be a albumIdOrUrl

      return authUrl;
    },
    showcaseAuthFetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      ...(options?.fallbackData && { fallbackData: options.fallbackData }),
    },
  );
};

const showcaseAuthFetcher: Fetcher<ShowcaseAuthResponse, string> = async (url) => {
  const res = await fetch(url);
  const parsed = await parseShowcaseAuthResponse(res);

  if (parsed.status === 404) {
    throw new NetworkError('Not found', res.status, res);
  }

  if (!parsed.ok) {
    // NetworkError.res is typed as Response but historically receives the parsed
    // auth JSON here — ViewerShowcasesAuthorization reads .res.errorCode/.res.metadata.
    throw new NetworkError(
      'A network error occurred',
      parsed.status,
      parsed.data as unknown as Response,
    );
  }

  return parsed.data!;
};

const serializeQuery = (options: Record<string, unknown>) => {
  const res = Object.entries(options)
    .filter(([, value]) => typeof value !== 'undefined')
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return res ? `?${res}` : '';
};

const getReferrerValue = () => {
  try {
    if (document?.referrer) {
      return document.referrer;
    }

    return document?.querySelector('meta[name="referrer"]')?.getAttribute('content') || '';
  } catch (error) {
    return '';
  }
};
